import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Edit2, DollarSign, Package, Trash2, Shield, Sword, Archive, Split, Combine } from 'lucide-react';
import { formatCurrency } from '../utils/currencyUtils';
import { useSettings } from '../contexts/SettingsContext';
import { useInventory } from '../contexts/InventoryContext';
import { useConfirm } from '../contexts/ConfirmContext'; // [UI-CONFIRM-UNIFICATION 2026-07-04]: styled async confirm for destructive Delete
import { Z } from '../constants/zLayers';
import useIsMobile from '../hooks/useIsMobile';
import { useModalEscape } from '../hooks/useModalEscape';
import { canUnattuneItem } from '../utils/cursedItemGates'; // [SUB-PLAN 1C ITEM 4 / 2026-05-23]: RAW DMG p.138 cursed-attunement lock
import { isItemIdentified } from '../utils/itemIdentificationGate'; // [SUB-PLAN 1C ITEM 3 / 2026-05-23]: Unidentified-loot gate
import { EQUIPMENT_SLOT_LABELS } from '../constants/enums'; // [SUB-PLAN 1R / 2026-05-29]: Canonical slot-value → user-facing label map
import SplitStackDialog from './SplitStackDialog'; // [UI-CONFIRM-UNIFICATION 2026-07-04]: replaces window.prompt for Split Stack

const ContextMenuComponent = ({
  // [PHASE 4B STEP 7 - 2025-10-25]: Only truly external function props
  // Functions still in App.jsx (not yet migrated)
  drinkPotion,
  // [NEW]: Shared Vault Support
  addToSharedVault = null,
  hasParty = false,
  // [NEW 2025-10-12]: Spellbook & Scribing System
  openSpellCopyDialog = null,
  // [UX-22 - NOV 16, 2025]: Read scroll to cast spell
  readScrollSpell = null,
  // [Spec B Task 13 — 2026-05-16]: Cast stored spell from Artificer's
  //   Spell-Storing Item. Injected from App.jsx — closure over
  //   playerStats / setPlayerStats / logActivity (closure built there
  //   to keep CharacterContext access out of this component).
  castStoredSpell = null,
  // [Spec B Task 13 — 2026-05-16]: Names of items currently bound as
  //   spell-storing-item active targets. Lets us conditionally show
  //   the "Cast stored spell" button only for the right items.
  spellStoringItemBoundIds = null
}) => {
  // [PHASE 4B STEP 7 - 2025-10-25]: ALL inventory state and functions from context
  const { settings } = useSettings();
  const {
    // UI State
    contextMenu,
    setContextMenu,
    setEditingItem,
    setOpenContainer,
    // Equipment/Item Functions
    sellItem,
    deleteItem,
    canEquip,
    getEquipSlots,
    equipItem,
    unequipItem,
    attuneToItem,
    unattuneItem,
    identifyItem, // [SUB-PLAN 1C ITEM 3 / 2026-05-23]: Reveal unidentified loot
    // [STACK INVESTIGATION APR 30 2026]: Stack-management actions surfaced
    //   from context menu so right-click is a discoverable entry point for
    //   stack inspection (Split / Combine).
    // [FIX MAY 2026]: Make Stackable removed from context menu entirely —
    //   it appeared on every non-stackable item including backpacks/armor.
    //   Stackable toggle now lives in EditItemDialog → Advanced Properties.
    inventory,
    splitStack,
    combineStacks
  } = useInventory();
  // [HOOKS ORDER FIX — APR 2026 Phase 3A.1]: useIsMobile() must run on every render.
  // [WHY]: Previously called AFTER `if (!contextMenu) return null;`, which would
  //        violate React's Rules of Hooks when contextMenu transitions null → value.
  const isMobile = useIsMobile();
  const confirm = useConfirm(); // [UI-CONFIRM-UNIFICATION 2026-07-04]

  // [UI-CONFIRM-UNIFICATION 2026-07-04]: Split Stack target. Lives outside
  // `contextMenu` so the dialog survives the context menu closing — the
  // Split Stack button both sets this AND calls setContextMenu(null) in the
  // same click, and the early-return below still renders this dialog.
  const [splitTarget, setSplitTarget] = useState(null);

  // [APR 2026]: Escape dismisses the context menu — same as click-outside.
  // [WHY]: Hook MUST come before the early return below to satisfy Rule 7.
  const closeContextMenu = useCallback(() => setContextMenu(null), [setContextMenu]);
  useModalEscape(Boolean(contextMenu), closeContextMenu);

  // [BUG FIX MAY 2026 / #207]: Viewport-clamp the desktop menu.
  // Pre-fix, contextMenu.x/.y came in as document coords (pageX/Y), and even
  // after the App.jsx producer was switched to clientX/Y there was still no
  // overflow check — a right-click in the lower-right corner placed the menu
  // half-off-screen. We measure the rendered menu in useLayoutEffect and flip
  // it above/left of the click point if it would overflow the viewport.
  // Mobile path is unchanged (its bottom-anchored sheet has no overflow risk).
  const menuRef = useRef(null);
  const [clamped, setClamped] = useState(null);
  useLayoutEffect(() => {
    if (!contextMenu || isMobile || !menuRef.current) {
      if (clamped !== null) setClamped(null);
      return;
    }
    const el = menuRef.current;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // contextMenu.x/.y are clientX/clientY (viewport coords) post-#207 producer fix.
    const cx = contextMenu.x;
    const cy = contextMenu.y;
    const left = (cx + w > vw) ? Math.max(8, cx - w) : cx;
    const top  = (cy + h > vh) ? Math.max(8, cy - h) : cy;
    if (!clamped || clamped.left !== left || clamped.top !== top) {
      setClamped({ left, top });
    }
  }, [contextMenu, isMobile, clamped]);

  // [UI-CONFIRM-UNIFICATION 2026-07-04]: Split Stack dialog must keep rendering
  // even after the menu itself closes (splitTarget outlives contextMenu), so
  // this early return renders the dialog instead of a bare null.
  if (!contextMenu) {
    return (
      <SplitStackDialog
        isOpen={!!splitTarget}
        item={splitTarget}
        onConfirm={(count) => {
          splitStack(splitTarget, count);
          setSplitTarget(null);
        }}
        onCancel={() => setSplitTarget(null)}
      />
    );
  }

  const item = contextMenu.item;
  const source = contextMenu.source;
  const isInventoryItem = source === 'inventory';
  const itemCanEquip = canEquip && canEquip(item);
  const itemEquipSlots = getEquipSlots && getEquipSlots(item);
  const isEquipped = item.equipped;

  // [STACK INVESTIGATION APR 30 2026]: Visibility flags for stack actions.
  //   Split appears when there's something to split (qty > 1).
  //   Combine appears when the inventory has at least one matching identical
  //   stackable item the user could merge with.
  // [FIX MAY 2026]: Removed Make Stackable from this menu — it fired for
  //   every non-stackable item (backpacks, plate armor, two-handed swords)
  //   which violated the user's mental model. Stackable opt-in now lives
  //   in EditItemDialog → Advanced Properties as a simple toggle + Max Stack
  //   number input. See SMOKE_TEST_TRIAGE_2026-05-04.md §P1-2.
  const itemQuantity = item.quantity || 1;
  const showSplitStack = isInventoryItem && item.stackable && itemQuantity > 1 && splitStack;
  const hasMatchingStack = isInventoryItem && item.stackable && Array.isArray(inventory) &&
    inventory.some(i =>
      i.id !== item.id &&
      i.stackable &&
      i.name === item.name &&
      i.category === item.category
    );
  const showCombineStacks = hasMatchingStack && combineStacks;
  
  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && (
        <div
          className="fixed inset-0 bg-black/50"
          style={{ zIndex: Z.CONTEXT_MENU_BACKDROP }}
          onClick={() => setContextMenu(null)}
        />
      )}
      
      {/* Context menu */}
      <div
        ref={menuRef}
        data-testid="context-menu"
        className={`fixed bg-gray-800 border border-gray-600 rounded shadow-2xl ${
          isMobile ? 'min-w-[240px] max-h-[80vh] overflow-y-auto' : ''
        }`}
        style={{
          zIndex: Z.CONTEXT_MENU,
          left: isMobile ? '10px' : (clamped ? clamped.left : contextMenu.x),
          right: isMobile ? '10px' : 'auto',
          bottom: isMobile ? '80px' : 'auto',
          top: isMobile ? 'auto' : (clamped ? clamped.top : contextMenu.y),
          // Hide for one frame until clamped layout settles to avoid flash off-screen
          visibility: (!isMobile && !clamped) ? 'hidden' : 'visible',
          transform: 'none'
        }}
      >
      <button
        onClick={() => {
          setEditingItem({ item: contextMenu.item, source: contextMenu.source });
          setContextMenu(null);
        }}
        className={`w-full px-4 text-left hover:bg-gray-700 text-white flex items-center transition-colors ${
          isMobile ? 'py-4 text-base min-h-[3rem]' : 'py-2'
        }`}
      >
        <Edit2 size={16} className="mr-2" />
        Edit Item
      </button>

      {/* [STACK INVESTIGATION APR 30 2026]: Split Stack — surfaces stack
          investigation directly from right-click. Prompts the player for
          the split count rather than silently halving so they can pick
          "give my friend 5 of my 12 arrows" without doing the math
          backwards. */}
      {showSplitStack && (
        <button
          onClick={() => {
            // [UI-CONFIRM-UNIFICATION 2026-07-04]: window.prompt replaced with
            // SplitStackDialog — set the split target, then close the menu.
            // The dialog is rendered outside this menu tree (see both return
            // branches) so it survives contextMenu going null.
            setSplitTarget(contextMenu.item);
            setContextMenu(null);
          }}
          className={`w-full px-4 text-left hover:bg-gray-700 text-blue-200 flex items-center transition-colors ${
            isMobile ? 'py-4 text-base min-h-[3rem]' : 'py-2'
          }`}
        >
          <Split size={16} className="mr-2" />
          Split Stack
        </button>
      )}

      {/* [STACK INVESTIGATION APR 30 2026]: Combine Stacks — visible only
          when the inventory has another matching stackable. Auto-merges
          all matching stacks into the source, honoring maxStack. */}
      {showCombineStacks && (
        <button
          onClick={() => {
            combineStacks(contextMenu.item);
            setContextMenu(null);
          }}
          className={`w-full px-4 text-left hover:bg-gray-700 text-blue-200 flex items-center transition-colors ${
            isMobile ? 'py-4 text-base min-h-[3rem]' : 'py-2'
          }`}
        >
          <Combine size={16} className="mr-2" />
          Combine Stacks
        </button>
      )}

      {/* [DELETED MAY 2026]: Make Stackable button — see destructure block
          above for the rationale. Toggle moved to EditItemDialog → Advanced
          Properties (stackable + maxStack inputs). */}

      {/* Drink Potion Option - [FIX JAN 13, 2026]: Property-based detection (ARC-014) */}
      {isInventoryItem && (item.category === 'consumables' || item.isPotion || item.effects?.some(e => e.type === 'healing' || e.type === 'damage' || e.type === 'ability')) && drinkPotion && (
        <button
          onClick={() => {
            drinkPotion(item);
            setContextMenu(null);
          }}
          className={`w-full px-4 text-left hover:bg-gray-700 text-white flex items-center transition-colors ${
            isMobile ? 'py-4 text-base' : 'py-2'
          }`}
        >
          <span className="mr-2">🧪</span>
          Drink Potion
        </button>
      )}

      {/* [NEW 2025-10-12]: Copy Spell to Spellbook Option for scrolls */}
      {isInventoryItem && (item.isScroll || (item.name?.toLowerCase().includes('scroll') && item.containedSpell)) && openSpellCopyDialog && (
        <button
          onClick={() => {
            openSpellCopyDialog(item);
            setContextMenu(null);
          }}
          className={`w-full px-4 text-left hover:bg-gray-700 text-purple-300 flex items-center transition-colors ${
            isMobile ? 'py-4 text-base min-h-[3rem]' : 'py-2'
          }`}
        >
          <span className="mr-2">📖</span>
          Copy to Spellbook
        </button>
      )}

      {/* [UX-22 - NOV 16, 2025]: Read Spell Scroll Option */}
      {/* [PURPOSE]: Cast spell from scroll (single use, consumes scroll) */}
      {/* [D&D 5e DMG p.200]: Reading a scroll casts the spell and destroys the scroll */}
      {isInventoryItem && (item.isScroll || (item.name?.toLowerCase().includes('scroll') && item.containedSpell)) && readScrollSpell && (
        <button
          onClick={() => {
            readScrollSpell(item);
            setContextMenu(null);
          }}
          className={`w-full px-4 text-left hover:bg-gray-700 text-amber-300 flex items-center transition-colors ${
            isMobile ? 'py-4 text-base min-h-[3rem]' : 'py-2'
          }`}
        >
          <span className="mr-2">📜</span>
          Read Scroll
        </button>
      )}

      {/* [Spec B Task 13 — 2026-05-16]: Cast stored spell action.
          Visible only when the item is the bound target of an active
          Spell-Storing Item selection (Artificer L11+ daily imbue).
          Click → spellCastingService.castStoredSpell decrements the
          pool by 1 + logs activity. Player adjudicates the spell's
          actual mechanical effects in v1 (no combat-tracker auto-roll). */}
      {isInventoryItem
        && castStoredSpell
        && Array.isArray(spellStoringItemBoundIds)
        && spellStoringItemBoundIds.includes(item.id || item.uid) && (
        <button
          onClick={() => {
            castStoredSpell(item.id || item.uid);
            setContextMenu(null);
          }}
          className={`w-full px-4 text-left hover:bg-gray-700 text-purple-300 flex items-center transition-colors ${
            isMobile ? 'py-4 text-base min-h-[3rem]' : 'py-2'
          }`}
        >
          <span className="mr-2">🔮</span>
          Cast Stored Spell
        </button>
      )}

      {/* Equipment Options */}
      {/* [SUB-PLAN 1R / 2026-05-29]: Label dispatch routes through the canonical
          EQUIPMENT_SLOT_LABELS map (constants/enums.js) instead of the prior
          ad-hoc `slot.replace('hand', ' Hand')` capitalisation. The Rule 11 / 17
          anti-pattern fix unifies labels across this component, ItemDialog, and
          EditItemDialog so a slot value like 'ioun' renders "Ioun Stone (orbits
          head)" everywhere it appears. The 'misc' value short-circuits the
          equip button — un-classified magical items are inventory-only. */}
      {isInventoryItem && itemCanEquip && !isEquipped && itemEquipSlots && itemEquipSlots.length > 0 && (
        <>
          {itemEquipSlots.filter(slot => slot !== 'misc').map(slot => {
            // Capitalise the slot value as a fallback so a newly-added slot
            // doesn't render a raw lowercase token in the UI.
            const fallbackLabel = slot.charAt(0).toUpperCase() + slot.slice(1);
            const slotLabel = EQUIPMENT_SLOT_LABELS[slot] || fallbackLabel;
            return (
              <button
                key={slot}
                onClick={() => equipItem && equipItem(item, slot)}
                className={`w-full px-4 text-left hover:bg-gray-700 text-white flex items-center transition-colors ${
            isMobile ? 'py-4 text-base min-h-[3rem]' : 'py-2'
          }`}
              >
                {slot === 'armor' || slot === 'shield' ? <Shield size={16} className="mr-2" /> : <Sword size={16} className="mr-2" />}
                Equip to {slotLabel}
              </button>
            );
          })}
        </>
      )}
      
      {isInventoryItem && isEquipped && (
        <button
          onClick={() => unequipItem && unequipItem(item)}
          className={`w-full px-4 text-left hover:bg-gray-700 text-white flex items-center transition-colors ${
          isMobile ? 'py-4 text-base min-h-[3rem]' : 'py-2'
        }`}
        >
          <Shield size={16} className="mr-2" />
          Unequip
        </button>
      )}

      {/* [SUB-PLAN 1C ITEM 3 / 2026-05-23]: Identify action — only visible
          when the item is currently unidentified. One click flips the flag
          and reveals the real name + description across inventory surfaces. */}
      {isInventoryItem && !isItemIdentified(item) && identifyItem && (
        <button
          onClick={() => {
            identifyItem(item);
            setContextMenu(null);
          }}
          className={`w-full px-4 text-left hover:bg-gray-700 text-amber-300 flex items-center transition-colors ${
            isMobile ? 'py-4 text-base min-h-[3rem]' : 'py-2'
          }`}
          title="Reveal this item's true name and properties"
        >
          <span className="mr-2">🔍</span>
          Identify
        </button>
      )}

      {/* Attunement Options */}
      {isInventoryItem && item.requiresAttunement && !item.attuned && attuneToItem && (
        <button
          onClick={() => {
            attuneToItem(item);
            setContextMenu(null);
          }}
          className={`w-full px-4 text-left hover:bg-gray-700 text-purple-300 flex items-center transition-colors ${
            isMobile ? 'py-4 text-base min-h-[3rem]' : 'py-2'
          }`}
        >
          <span className="mr-2">🔮</span>
          Attune to Item
        </button>
      )}

      {isInventoryItem && item.attuned && unattuneItem && (() => {
        // [SUB-PLAN 1C ITEM 4 / 2026-05-23]: RAW DMG p.138 cursed lock — render
        //   the Unattune action as a disabled, distinctly-styled "Bound by Curse"
        //   entry instead of silently swallowing the click. Keeps the option
        //   discoverable (player can see WHY they're locked) without leaving a
        //   dead-feeling no-op button. Showing the option even when locked is
        //   intentional — Spec 5 D4 favors discoverability over hiding.
        const locked = !canUnattuneItem(item);
        return (
          <button
            onClick={() => {
              if (locked) {
                // No-op: the click still closes the menu so it doesn't feel sticky,
                // but InventoryContext.unattuneItem ALSO short-circuits + surfaces
                // showStatus on its own — so even a hypothetical re-enable path
                // remains safe.
                setContextMenu(null);
                return;
              }
              unattuneItem(item);
              setContextMenu(null);
            }}
            disabled={locked}
            className={
              locked
                ? `w-full px-4 text-left text-gray-500 flex items-center cursor-not-allowed ${
                    isMobile ? 'py-4 text-base min-h-[3rem]' : 'py-2'
                  }`
                : `w-full px-4 text-left hover:bg-gray-700 text-gray-300 flex items-center transition-colors ${
                    isMobile ? 'py-4 text-base min-h-[3rem]' : 'py-2'
                  }`
            }
            title={
              locked
                ? '🔒 Cursed — only Remove Curse (or similar magic) can free you. DMG p.138.'
                : 'Remove attunement from this item'
            }
          >
            <span className="mr-2">{locked ? '🔒' : '🔓'}</span>
            {locked ? 'Bound by Curse' : 'Unattune'}
          </button>
        );
      })()}

      {contextMenu.source === 'inventory' && (
        <button 
          onClick={() => sellItem(contextMenu.item)}
          className={`w-full px-4 text-left hover:bg-gray-700 text-white flex items-center transition-colors ${
          isMobile ? 'py-4 text-base min-h-[3rem]' : 'py-2'
        }`}
        >
          <DollarSign size={16} className="mr-2" />
          Sell ({(() => {
            // [BUG FIX 2025-11-10]: Guard against undefined value (prevents NaN in sell display)
            let itemValue = contextMenu.item.value || 0;
            if (contextMenu.item.isContainer) {
              if (contextMenu.item.getTotalValue) {
                itemValue = contextMenu.item.getTotalValue();
              } else if (contextMenu.item.containedItems) {
                // Fallback calculation if method doesn't exist
                itemValue += contextMenu.item.containedItems.reduce((sum, containedItem) => {
                  return sum + (containedItem.value || 0);
                }, 0);
              }
            }
            return formatCurrency((itemValue || 0) * settings.defaultSellRatio);
          })()})
        </button>
      )}
      {contextMenu.item.isContainer && (
        <button 
          onClick={() => {
            setOpenContainer(contextMenu.item);
            setContextMenu(null);
          }}
          className={`w-full px-4 text-left hover:bg-gray-700 text-white flex items-center transition-colors ${
          isMobile ? 'py-4 text-base min-h-[3rem]' : 'py-2'
        }`}
        >
          <Package size={16} className="mr-2" />
          Open Container
        </button>
      )}

      {/* [NEW]: Add to Shared Vault Option */}
      {isInventoryItem && hasParty && addToSharedVault && (
        <button
          onClick={() => {
            addToSharedVault(contextMenu.item);
            setContextMenu(null);
          }}
          className={`w-full px-4 text-left hover:bg-gray-700 text-white flex items-center transition-colors border-t border-gray-700 ${
            isMobile ? 'py-4 text-base min-h-[3rem]' : 'py-2'
          }`}
        >
          <Archive size={16} className="mr-2 text-green-400" />
          <span className="text-green-400">Add to Party Vault</span>
        </button>
      )}

      <button
        onClick={async () => {
          // [UI-CONFIRM-UNIFICATION 2026-07-04]: gate destructive delete with
          // the styled async confirm (audit G-1/B-10 — character delete
          // confirms, item delete didn't, and nothing is recoverable).
          // Close the menu first, then confirm, then delete — snapshot the
          // target into locals since setContextMenu(null) below doesn't
          // affect this already-created closure's `contextMenu` binding.
          const targetItem = contextMenu.item;
          const targetSource = contextMenu.source;
          setContextMenu(null);
          const qty = targetItem.quantity || 1;
          const itemLabel = qty > 1 ? `${qty} ${targetItem.name}` : targetItem.name;
          if (await confirm({
            title: 'Delete Item',
            message: `Delete "${itemLabel}"? This cannot be undone.`,
            confirmText: 'Delete',
            confirmColor: 'red',
          })) {
            deleteItem(targetItem, targetSource);
          }
        }}
        className={`w-full px-4 text-left hover:bg-gray-700 text-white flex items-center transition-colors ${
          isMobile ? 'py-4 text-base min-h-[3rem]' : 'py-2'
        }`}
      >
        <Trash2 size={16} className="mr-2" />
        Delete
      </button>
    </div>

    {/* [UI-CONFIRM-UNIFICATION 2026-07-04]: rendered here too (not just the
        !contextMenu early-return branch above) so Split Stack still shows
        its dialog for the one frame where contextMenu is still truthy,
        pre-close. */}
    <SplitStackDialog
      isOpen={!!splitTarget}
      item={splitTarget}
      onConfirm={(count) => {
        splitStack(splitTarget, count);
        setSplitTarget(null);
      }}
      onCancel={() => setSplitTarget(null)}
    />
    </>
  );
};

export default ContextMenuComponent;